import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  DocumentTextIcon, 
  PencilIcon, 
  EyeIcon,
  AcademicCapIcon,
  BookOpenIcon,
  UsersIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import useCategories from '../hooks/useCategories';
import useUsers from '../hooks/useUsers';

const ClassroomModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  classroom, 
  mode = 'add', // 'add', 'edit', 'view'
  currentUser = null,
  userRole = null
}) => {
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    category: '',
    description: '',
    status: 'active',
    memberCount: 0,
    maxMembers: 50,
    createdBy: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'admin', 'author'

  // Get categories from Firestore
  const { categories, loading: categoriesLoading } = useCategories();
  
  // Get users for author selection
  const { users, authors, admins, loading: usersLoading } = useUsers();
  
  // Filter users based on active tab
  const filteredUsers = users.filter(user => {
    if (!user || !user.id) {
      console.log("Invalid user found:", user);
      return false;
    }
    
    // If 'all' tab is selected, show all users
    if (activeTab === 'all') {
      return true;
    }
    
    // Check if user has the role for the active tab
    if (user.role) {
      if (Array.isArray(user.role)) {
        return user.role.some(role => {
          const normalizedRole = role?.toString().toLowerCase().trim();
          if (activeTab === 'admin') {
            return normalizedRole === 'admin';
          } else if (activeTab === 'author') {
            return normalizedRole === 'author' || normalizedRole === 'instructor' || normalizedRole === 'teacher';
          }
          return false;
        });
      } else {
        const normalizedRole = user.role.toString().toLowerCase().trim();
        if (activeTab === 'admin') {
          return normalizedRole === 'admin';
        } else if (activeTab === 'author') {
          return normalizedRole === 'author' || normalizedRole === 'instructor' || normalizedRole === 'teacher';
        }
      }
    }
    
    return false;
  });
  
  // Debug: Log users data
  useEffect(() => {
    console.log("=== ClassroomModal Debug ===");
    console.log("Users loaded:", users.length);
    console.log("Users loading:", usersLoading);
    console.log("Active tab:", activeTab);
    console.log("Filtered users:", filteredUsers.length);
    console.log("Authors count:", authors.length);
    console.log("Admins count:", admins.length);
    
    if (users.length > 0) {
      console.log("Sample user data:", users[0]);
    }
    
    if (filteredUsers.length > 0) {
      console.log("Filtered users with IDs:", filteredUsers.map(u => ({ id: u.id, name: u.name, email: u.email, role: u.role })));
    }
    
    console.log("=== End Debug ===");
  }, [users, filteredUsers, activeTab, usersLoading, authors, admins]);

  const statuses = [
    { value: 'active', label: 'Active', color: 'text-green-600' },
    { value: 'inactive', label: 'Inactive', color: 'text-red-600' },
    { value: 'maintenance', label: 'Maintenance', color: 'text-yellow-600' },
    { value: 'archived', label: 'Archived', color: 'text-gray-600' }
  ];

  useEffect(() => {
    if (classroom && mode !== 'add') {
      setFormData({
        name: classroom.name || '',
        subject: classroom.subject || '',
        category: classroom.category || '',
        description: classroom.description || '',
        status: classroom.status || 'active',
        memberCount: classroom.memberCount || 0,
        maxMembers: classroom.maxMembers || 50,
        createdBy: classroom.createdBy || '',
        createdAt: classroom.createdAt || new Date().toISOString(),
        updatedAt: classroom.updatedAt || new Date().toISOString()
      });
    } else {
      setFormData({
        name: '',
        subject: '',
        category: '',
        description: '',
        status: 'active',
        memberCount: 0,
        maxMembers: 50,
        createdBy: currentUser?.uid || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
    setErrors({});
  }, [classroom, mode, currentUser]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Classroom name is required';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    
    if (formData.memberCount < 0) {
      newErrors.memberCount = 'Member count cannot be negative';
    }
    
    if (formData.maxMembers < 1) {
      newErrors.maxMembers = 'Maximum members must be at least 1';
    }
    
    if (formData.memberCount > formData.maxMembers) {
      newErrors.memberCount = 'Member count cannot exceed maximum members';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Check if user is trying to edit a classroom they didn't create
    if (mode === 'edit' && classroom && classroom.createdBy && currentUser && userRole !== 'admin' && classroom.createdBy !== currentUser.uid) {
      console.error('User cannot edit classroom they did not create');
      return;
    }

    setLoading(true);
    try {
      const dataToSave = {
        ...formData,
        updatedAt: new Date().toISOString()
      };
      
      if (mode === 'add') {
        dataToSave.createdAt = new Date().toISOString();
      }
      
      await onSave(dataToSave);
    } catch (error) {
      console.error('Error saving classroom:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {mode === 'view' ? (
                  <EyeIcon className="h-6 w-6 text-white mr-3" />
                ) : mode === 'edit' ? (
                  <PencilIcon className="h-6 w-6 text-white mr-3" />
                ) : (
                  <AcademicCapIcon className="h-6 w-6 text-white mr-3" />
                )}
                <h3 className="text-xl font-semibold text-white">
                  {mode === 'add' ? 'Create New Classroom' : mode === 'edit' ? 'Edit Classroom' : 'View Classroom'}
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            {classroom?.id && (
              <div className="mt-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white bg-opacity-20 text-white">
                  ID: {classroom.id}
                </span>
              </div>
            )}
          </div>
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white px-6 py-6">
            <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="text-lg font-medium text-gray-900 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 mr-2 text-blue-600" />
                  Basic Information
                </h4>
                
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Classroom Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    disabled={mode === 'view'}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                      errors.name ? 'border-red-300' : 'border-gray-300'
                    } ${mode === 'view' ? 'bg-gray-50 text-gray-500' : 'bg-white'}`}
                    placeholder="Enter classroom name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    disabled={mode === 'view'}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                      errors.subject ? 'border-red-300' : 'border-gray-300'
                    } ${mode === 'view' ? 'bg-gray-50 text-gray-500' : 'bg-white'}`}
                    placeholder="Enter subject (e.g., Advanced Mathematics)"
                  />
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-600">{errors.subject}</p>
                  )}
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    disabled={mode === 'view' || categoriesLoading}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                      errors.category ? 'border-red-300' : 'border-gray-300'
                    } ${mode === 'view' ? 'bg-gray-50 text-gray-500' : 'bg-white'} ${categoriesLoading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  >
                    <option value="">Select a category</option>
                    {categoriesLoading ? (
                      <option value="" disabled>Loading categories...</option>
                    ) : (
                      categories.map(category => (
                        <option key={category.id} value={category.name}>{category.name}</option>
                      ))
                    )}
                  </select>
                  {errors.category && (
                    <p className="mt-1 text-sm text-red-600">{errors.category}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    disabled={mode === 'view'}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                      errors.description ? 'border-red-300' : 'border-gray-300'
                    } ${mode === 'view' ? 'bg-gray-50 text-gray-500' : 'bg-white'}`}
                    placeholder="Enter classroom description (optional)"
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                  )}
                </div>
              </div>

              {/* Settings */}
              <div className="space-y-4">
                <h4 className="text-lg font-medium text-gray-900 flex items-center">
                  <BookOpenIcon className="h-5 w-5 mr-2 text-green-600" />
                  Settings
                </h4>
                
                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    disabled={mode === 'view'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                  >
                    {statuses.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Member Count */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Members
                    </label>
                    <input
                      type="number"
                      value={formData.memberCount}
                      onChange={(e) => handleInputChange('memberCount', parseInt(e.target.value) || 0)}
                      disabled={mode === 'view'}
                      min="0"
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.memberCount ? 'border-red-300' : 'border-gray-300'
                      } ${mode === 'view' ? 'bg-gray-50 text-gray-500' : 'bg-white'}`}
                    />
                    {errors.memberCount && (
                      <p className="mt-1 text-sm text-red-600">{errors.memberCount}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Members
                    </label>
                    <input
                      type="number"
                      value={formData.maxMembers}
                      onChange={(e) => handleInputChange('maxMembers', parseInt(e.target.value) || 1)}
                      disabled={mode === 'view'}
                      min="1"
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.maxMembers ? 'border-red-300' : 'border-gray-300'
                      } ${mode === 'view' ? 'bg-gray-50 text-gray-500' : 'bg-white'}`}
                    />
                    {errors.maxMembers && (
                      <p className="mt-1 text-sm text-red-600">{errors.maxMembers}</p>
                    )}
                  </div>
                </div>

                {/* Author Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Author
                  </label>
                  
                  {/* User Filter Tabs */}
                  <div className="mb-3">
                    <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                      <button
                        type="button"
                        onClick={() => setActiveTab('all')}
                        disabled={mode === 'view' || usersLoading}
                        className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          activeTab === 'all'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        } ${mode === 'view' || usersLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        All Users ({users.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveTab('admin')}
                        disabled={mode === 'view' || usersLoading}
                        className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          activeTab === 'admin'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        } ${mode === 'view' || usersLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        Admins ({admins.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveTab('author')}
                        disabled={mode === 'view' || usersLoading}
                        className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          activeTab === 'author'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        } ${mode === 'view' || usersLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        Authors ({authors.length})
                      </button>
                    </div>
                  </div>
                  
                  {/* User Selection Dropdown */}
                  <select
                    value={formData.createdBy}
                    onChange={(e) => handleInputChange('createdBy', e.target.value)}
                    disabled={mode === 'view' || usersLoading}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                      mode === 'view' ? 'bg-gray-50 text-gray-500' : 'bg-white'
                    } ${usersLoading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  >
                    <option value="">Select a user</option>
                    {usersLoading ? (
                      <option value="" disabled>Loading users...</option>
                    ) : filteredUsers.length > 0 ? (
                      filteredUsers.map(user => {
                        const roleText = Array.isArray(user.role) ? user.role.join(', ') : user.role || 'User';
                        return (
                          <option key={user.id} value={user.id}>
                            {user.name || user.email || 'Unknown User'} • {user.email} • {roleText}
                          </option>
                        );
                      })
                    ) : (
                      <option value="" disabled>No users found</option>
                    )}
                  </select>
                  
                  {/* Results count */}
                  {!usersLoading && (
                    <div className="mt-1 text-xs text-gray-500">
                      {activeTab === 'all' ? (
                        `Showing all ${users.length} users`
                      ) : activeTab === 'admin' ? (
                        `Showing ${filteredUsers.length} admin users`
                      ) : activeTab === 'author' ? (
                        `Showing ${filteredUsers.length} author users`
                      ) : (
                        `Showing ${filteredUsers.length} users`
                      )}
                      {filteredUsers.length === 0 && activeTab !== 'all' && (
                        <span className="text-red-500 font-medium ml-2">
                          No {activeTab} users found
                        </span>
                      )}
                    </div>
                  )}
                  
                  {currentUser && formData.createdBy === currentUser.uid && (
                    <div className="mt-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                        You
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Subcollections Info */}
              <div className="space-y-4">
                <h4 className="text-lg font-medium text-gray-900 flex items-center">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2 text-purple-600" />
                  Subcollections
                </h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center">
                      <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-600 mr-2" />
                      <div>
                        <h5 className="font-medium text-blue-900">Messages</h5>
                        <p className="text-sm text-blue-700">Public classroom discussions</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center">
                      <UsersIcon className="h-5 w-5 text-purple-600 mr-2" />
                      <div>
                        <h5 className="font-medium text-purple-900">Private Messages</h5>
                        <p className="text-sm text-purple-700">Direct student communications</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse border-t">
            {mode !== 'view' ? (
              <>
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full inline-flex justify-center items-center rounded-lg border border-transparent shadow-sm px-6 py-3 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    'Save Classroom'
                  )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-6 py-3 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-6 py-3 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassroomModal;
